import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../../../../store';
import {
  EScheduleSteps,
  saveSelectedDate,
  saveSelectedDay,
  updateScheduleSteps
} from '../../../../features/schedule/schedule-slice';
import { clearCalendarSelected } from '../../../../features/calendar/calendar-slice';
import { ScheduleAnalyticsTrackerInstance } from '../../../../lib/gtm';
import { FeaturedScheduleDay } from '../../../../services/availableTimes.service';

type FeaturedAppointmentsViewModelProps = {
  featuredScheduleDays: FeaturedScheduleDay[];
  onFinished?: () => void;
};

export default function FeaturedAppointmentsViewModel({
  featuredScheduleDays,
  onFinished
}: FeaturedAppointmentsViewModelProps) {
  const dispatch = useDispatch();
  const {
    schedule: {
      dateStart,
      ui: { previousStep }
    },
    calendar: { calendarsAvailables, calendarSelected }
  } = useSelector((state: RootState) => state);

  useEffect(() => {
    ScheduleAnalyticsTrackerInstance.setProperties({
      featuredSchedulesDisplay: 'yes',
      featuredSchedulesSlot: featuredScheduleDays.flatMap(day => day.times)
    });
  }, [featuredScheduleDays]);

  const onClose = () => {
    dispatch(updateScheduleSteps({ currentStep: EScheduleSteps.idle, previousStep: EScheduleSteps.idle }));
    dispatch(clearCalendarSelected());
    ScheduleAnalyticsTrackerInstance.clear();
  };

  const onChooseCalendar = () => {
    if (onFinished) {
      onFinished();
    }
    ScheduleAnalyticsTrackerInstance.setProperties({
      scheduleSelectionFlow: 'default'
    });
    dispatch(saveSelectedDate(''));
    dispatch(saveSelectedDay(''));
    dispatch(
      updateScheduleSteps({
        currentStep: EScheduleSteps.openCalendar,
        previousStep: EScheduleSteps.featuredAppointments
      })
    );
  };

  const onBack = () => {
    dispatch(updateScheduleSteps({ currentStep: previousStep, previousStep }));
    ScheduleAnalyticsTrackerInstance.clear();
  };

  const canContinue = Boolean(dateStart) && Boolean(calendarSelected);

  const onContinue = () => {
    if (!canContinue) return;
    ScheduleAnalyticsTrackerInstance.setProperties({
      scheduleSelectionFlow: 'featured_schedules'
    });
    dispatch(
      updateScheduleSteps({
        currentStep: EScheduleSteps.confirmSchedule,
        previousStep: EScheduleSteps.featuredAppointments
      })
    );
  };

  return {
    dateStart,
    calendarsAvailables,
    canContinue,
    onClose,
    onChooseCalendar,
    onBack,
    onContinue
  };
}
